import React, { useState, useEffect } from 'react';
import { TransactionEntity } from '../../models/TransactionEntity';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import {
  useCreateTransactionEntity,
  useUpdateTransactionEntity,
  CreateTransactionEntityData,
  UpdateTransactionEntityData,
} from '../../api/transactions';
import arrowLeft from '../../assets/images/arrow_back.png';

interface NewTransactionEntitySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEntity: (entity: TransactionEntity) => void;
  entityToEdit?: TransactionEntity | null;
  isEditMode?: boolean;
}

const NewTransactionEntitySidebar: React.FC<NewTransactionEntitySidebarProps> = ({
  isOpen,
  onClose,
  onSelectEntity,
  entityToEdit = null,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<Partial<TransactionEntity>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const createEntityMutation = useCreateTransactionEntity();
  const updateEntityMutation = useUpdateTransactionEntity();
  const loading = createEntityMutation.isPending || updateEntityMutation.isPending;

  // Category options
  const categoryOptions = [
    { value: 'action', label: 'Action' },
    { value: 'object', label: 'Object' },
    { value: 'media', label: 'Payment Media' },
  ];

  // Type options: credit, debit, tradable, non_tradable, payable, third_party (or null for object/media)
  const getTypeOptions = (category: string) => {
    switch (category) {
      case 'action':
        return [
          { value: 'debit', label: 'Debit (Expense / Purchase / Outgoing)' },
          { value: 'credit', label: 'Credit (Income / Sale / Received)' },
        ];
      case 'object':
      case 'indirect_object':
        return [
          { value: 'tradable', label: 'Tradable (e.g. goods)' },
          { value: 'non_tradable', label: 'Non-tradable' },
          { value: 'payable', label: 'Payable (e.g. loans, credit cards)' },
          { value: 'third_party', label: 'Third party (e.g. vendor)' },
          { value: '', label: 'None' },
        ];
      case 'media':
        return [
          { value: '', label: 'None' },
        ];
      default:
        return [{ value: '', label: 'None' }];
    }
  };

  // Initialize form data when entityToEdit changes
  useEffect(() => {
    if (isEditMode && entityToEdit) {
      // Pre-fill form with entity data for editing
      setFormData({
        ...entityToEdit,
      });
    } else {
      // Initialize empty form for new entity
      setFormData({
        transaction_entity_category: 'action',
        transaction_entity_type: 'debit',
        linked_to_kpi: false,
        position: 1,
      });
    }
  }, [entityToEdit, isEditMode]);

  const guessActionType = (name: string): string => {
    const n = name.toLowerCase();
    if (/(sold|sale|received|receipt|income|earned)/.test(n)) return 'credit';
    if (/(bought|buy|purchas|paid|pay|expense|spent|cost)/.test(n)) return 'debit';
    return 'debit';
  };

  const handleInputChange = (field: keyof TransactionEntity, value: any) => {
    setFormData(prev => {
      const next: Partial<TransactionEntity> = {
        ...prev,
        [field]: value,
      };

      if (field === 'transaction_entity_category') {
        next.transaction_entity_type = getTypeOptions(value)[0]?.value || '';
      }

      // Suggest credit/debit from action name (Bought → debit, Sold → credit)
      if (
        field === 'transaction_entity_name' &&
        (prev.transaction_entity_category || 'action') === 'action' &&
        typeof value === 'string'
      ) {
        next.transaction_entity_type = guessActionType(value);
      }

      return next;
    });

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.transaction_entity_name?.trim()) {
      newErrors.transaction_entity_name = 'Name is required';
    }

    if (!formData.transaction_entity_category) {
      newErrors.transaction_entity_category = 'Category is required';
    }

    if (formData.transaction_entity_category === 'action' && !formData.transaction_entity_type?.trim()) {
      newErrors.transaction_entity_type = 'Type is required for actions (Credit or Debit)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (isEditMode && entityToEdit?.id) {
      const typeValue = formData.transaction_entity_type?.trim();
      const updateData: UpdateTransactionEntityData = {};
      if (formData.transaction_entity_name !== undefined) updateData.transaction_entity_name = formData.transaction_entity_name;
      if (formData.transaction_entity_description !== undefined) updateData.transaction_entity_description = formData.transaction_entity_description || undefined;
      if (formData.transaction_entity_category !== undefined) updateData.transaction_entity_category = formData.transaction_entity_category;
      if (typeValue) updateData.transaction_entity_type = typeValue;
      else if (formData.transaction_entity_category !== 'action') updateData.transaction_entity_type = null;
      if (formData.transaction_entity_past_tense !== undefined) updateData.transaction_entity_past_tense = formData.transaction_entity_past_tense?.trim() || null;
      if (formData.linked_to_kpi !== undefined) updateData.linked_to_kpi = formData.linked_to_kpi;
      updateData.position = formData.position ?? entityToEdit.position ?? 1;

      updateEntityMutation.mutate(
        { entityId: entityToEdit.id, data: updateData },
        {
          onSuccess: (result) => {
            onSelectEntity(result.entity);
            onClose();
          },
          onError: (error) => {
            setErrors(prev => ({
              ...prev,
              submit: error.message || 'Failed to update entity. Please try again.',
            }));
          },
        }
      );
      return;
    }

    const typeValue = formData.transaction_entity_type?.trim();
    const apiData: CreateTransactionEntityData = {
      transaction_entity_name: formData.transaction_entity_name || '',
      transaction_entity_description: formData.transaction_entity_description || undefined,
      transaction_entity_category: formData.transaction_entity_category as CreateTransactionEntityData['transaction_entity_category'],
      ...(typeValue ? { transaction_entity_type: typeValue } : {}),
      transaction_entity_past_tense: formData.transaction_entity_past_tense?.trim() || undefined,
      linked_to_kpi: formData.linked_to_kpi || false,
      position: formData.position ?? 1,
    };

    createEntityMutation.mutate(
      { data: apiData },
      {
        onSuccess: (result) => {
          onSelectEntity(result.entity);
          onClose();
        },
        onError: (error) => {
          setErrors(prev => ({
            ...prev,
            submit: error.message || 'Failed to create entity. Please try again.',
          }));
        },
      }
    );
  };

  const handleCancel = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'action': return 'Action';
      case 'object': return 'Object';
      case 'media': return 'Payment Media';
      default: return 'Category';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - Only on mobile */}
      <div
        className="fixed inset-0 bg-gray-700 bg-opacity-50 z-40 lg:hidden"
        onClick={handleCancel}
      />

      {/* Sidebar */}
      <div className="fixed inset-y-0 right-0 z-50 p-4 sm:p-2 w-full lg:relative lg:w-full bg-light shadow-lg lg:shadow-none transform transition-transform duration-300 ease-in-out lg:transform-none rounded-md h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-grey_light">
          <div className="flex items-center">
            <button
              onClick={handleCancel}
              className="lg:hidden flex items-center text-primary_variant hover:text-dark"
              aria-label="Close"
            >
              <img
                src={arrowLeft}
                alt="Back"
                className="object-contain w-5 h-5 mr-2"
              />
            </button>
            <h3 className="text-lg font-medium">
              {isEditMode ? 'Edit Transaction Entity' : 'New Transaction Entity'}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="hidden lg:block p-1 hover:bg-bg_grey_light rounded-full transition-colors duration-150"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-grey_dark hover:text-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-full overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Name Field - Horizontal Layout */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                Name <span className="text-danger_dark">*</span>
              </label>
              <div className="flex-1">
                <Input
                  type="text"
                  attributes={{
                    placeholder: "",
                    value: formData.transaction_entity_name || '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('transaction_entity_name', e.target.value)
                  }}
                  className={`w-full ${errors.transaction_entity_name ? 'border-danger_dark' : ''}`}
                />
                {errors.transaction_entity_name && (
                  <p className="mt-1 text-sm text-danger">{errors.transaction_entity_name}</p>
                )}
              </div>
            </div>

            {/* Description Field - Horizontal Layout */}
            <div className="flex items-start gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32 pt-2">
                Description
              </label>
              <div className="flex-1">
                <Input
                  type="textarea"
                  attributes={{
                    placeholder: "",
                    value: formData.transaction_entity_description || '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('transaction_entity_description', e.target.value),
                    rows: 3
                  }}
                  className="w-full"
                />
              </div>
            </div>

            {/* Category Field - Horizontal Layout */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                Category <span className="text-danger_dark">*</span>
              </label>
              <div className="flex-1">
                <Select
                  options={categoryOptions}
                  value={formData.transaction_entity_category || ''}
                  onChange={(value) => handleInputChange('transaction_entity_category', value)}
                  placeholder=""
                  error={!!errors.transaction_entity_category}
                />
                {errors.transaction_entity_category && (
                  <p className="mt-1 text-sm text-danger">{errors.transaction_entity_category}</p>
                )}
              </div>
            </div>

            {/* Type Field - Horizontal Layout */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                Type <span className="text-danger_dark">*</span>
              </label>
              <div className="flex-1">
                <Select
                  options={getTypeOptions(formData.transaction_entity_category || '')}
                  value={formData.transaction_entity_type || ''}
                  onChange={(value) => handleInputChange('transaction_entity_type', value)}
                  placeholder=""
                  error={!!errors.transaction_entity_type}
                />
                {errors.transaction_entity_type && (
                  <p className="mt-1 text-sm text-danger">{errors.transaction_entity_type}</p>
                )}
              </div>
            </div>

            {/* Past Tense Field (only for actions) - Horizontal Layout */}
            {formData.transaction_entity_category === 'action' && (
              <div className="flex items-center gap-4 mb-4">
                <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                  Past Tense
                </label>
                <div className="flex-1">
                  <Input
                    type="text"
                    attributes={{
                      placeholder: "",
                      value: formData.transaction_entity_past_tense || '',
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('transaction_entity_past_tense', e.target.value),
                    }}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {/* Position Field - Horizontal Layout */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                Position
              </label>
              <div className="flex-1">
                <Input
                  type="number"
                  attributes={{
                    placeholder: "",
                    value: formData.position ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('position', e.target.value === '' ? undefined : parseInt(e.target.value)),
                    min: 1,
                  }}
                  className="w-full"
                />
              </div>
            </div>

            {/* KPI Link Field - Horizontal Layout */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-grey_dark whitespace-nowrap w-32">
                
              </label>
              <div className="flex-1">
                <label className="flex items-center cursor-pointer checkbox">
                  <input
                    type="checkbox"
                    id="linked_to_kpi"
                    checked={formData.linked_to_kpi || false}
                    onChange={(e) => handleInputChange('linked_to_kpi', e.target.checked)}
                    className="mr-2 checkbox"
                  />
                  <span className="checkmark"></span>
                  <span className="text-sm text-grey_dark">Link to KPI</span>
                </label>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-danger">{errors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <Button
                onClick={handleCancel}
                variant="filled-primary"
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="filled-primary"
                className="flex-1"
                disabled={loading}
              >
                {loading
                  ? (isEditMode ? 'Updating...' : 'Creating...')
                  : (isEditMode ? 'Update Entity' : 'Create Entity')}
              </Button>
            </div>

            {/* Help Text */}
            <div className="pt-4 border-t border-grey_light">
              <p className="text-sm text-grey_dark">
                Transaction entities are reusable components for your transactions.
                {formData.transaction_entity_category === 'action' && " For actions, the past tense is used in transaction history."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewTransactionEntitySidebar;